import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEvents, useLiveBoard, useNow, usePublicData } from '../lib/hooks';
import { fmtTime, fmtWeekday } from '../lib/format';
import {
  buildFeed, daysText, kindsText, mealHeadline, mealTitle, noticeContext, places, pujoCountdown, startsIn, timeLeft, today, TOPIC_LABEL,
  venueStatusText, whenText, type Countdown, type LiveVenue,
} from '../lib/live';
import type { MenuItem, Notice, Sitting, Venue } from '../lib/types';
import { VegMark } from './ui';

/** The live feed of the live events' meals, from public data, the live board and the clock. */
export function useLiveFeed(refetchMs?: number) {
  const { menu, venues, settings } = usePublicData();
  const ev = useEvents();
  const board = useLiveBoard(refetchMs);
  const now = useNow(15_000);
  const sittings = ev.current;
  // Booking cutoffs only for events still taking bookings.
  const bookable = useMemo(() => sittings.filter((s) => ev.byId.get(s.event_id)?.booking_open !== false), [sittings, ev.byId]);
  const feed = useMemo(() => {
    const f = buildFeed({ sittings, menu: menu.data ?? [], venues: venues.data ?? [], board: board.data, settings: settings.data, now });
    return { ...f, cutoffs: f.cutoffs.filter((s) => bookable.includes(s)) };
  }, [sittings, bookable, menu.data, venues.data, board.data, settings.data, now]);
  const eventName = ev.live.map((e) => e.name).join(' · ');
  const countdown = useMemo(() => pujoCountdown(sittings, now, ev.byId), [sittings, now, ev.byId]);
  return {
    feed, now, countdown, eventName, ready: ev.ready && !!menu.data && !!venues.data,
    venues: venues.data ?? [], menu: menu.data ?? [], sittings,
  };
}

/** One venue of a meal: what is served there, where, and its state. Opens the venue's details. */
export function VenueChips({ venues, sitting, link = true }: { venues: LiveVenue[]; sitting: Sitting; link?: boolean }) {
  return (
    <ul className="vchips">
      {venues.map((v) => {
        const inner = (
          <>
            <span className="vchip-top">{v.kinds.map((k) => <VegMark key={k} kind={k} />)}{kindsText(v.kinds)} · <b>{v.venue.name}</b></span>
            <span className="vchip-s">{venueStatusText(v, sitting)}</span>
          </>
        );
        return (
          <li key={v.venue.id}>
            {link ? <Link className={`vchip st-${v.status}`} to={`/venues#${v.venue.id}`}>{inner}</Link> : <span className={`vchip st-${v.status}`}>{inner}</span>}
          </li>
        );
      })}
    </ul>
  );
}

const TONE_TAG: Partial<Record<Notice['tone'], string>> = { good: 'Good news', delay: 'Delay', alert: 'Important' };

/**
 * A committee notice with what it is about, when and where. With a start time it is an event card
 * (time, "in 25 min" or "Now on"); otherwise a line with its context underneath.
 */
export function NoticeCard({ n, sittings, venues, now }: {
  n: Pick<Notice, 'body' | 'tone' | 'topic' | 'sitting_id' | 'venue_id' | 'starts_at' | 'source'> & { at?: string };
  sittings: Sitting[]; venues: Venue[]; now: number;
}) {
  const c = noticeContext(n, sittings, venues, now);
  const tag = n.source === 'auto' ? 'Update' : TONE_TAG[n.tone] ?? TOPIC_LABEL[n.topic];
  // The tag already says the topic when it isn't a tone, so the context starts with the meal or topic only when useful.
  const about = c.sitting ? mealTitle(c.sitting) : TONE_TAG[n.tone] && n.source !== 'auto' ? TOPIC_LABEL[n.topic] : null;
  if (n.starts_at) {
    const time = fmtTime(n.starts_at).split(' ');
    const today = whenText(n.starts_at, now) === fmtTime(n.starts_at);
    return (
      <div className={`ncard topic-${n.topic} tone-${n.tone}`}>
        <span className="ncard-time">
          {!today && <small>{whenText(n.starts_at, now).split(',')[0]}</small>}
          <b>{time[0]}</b><small>{time[1]}</small>
        </span>
        <span className="ncard-main">
          <span className="ncard-eyebrow">{[tag, about, c.venue?.name].filter(Boolean).join(' · ')}</span>
          <span className="ncard-body">{n.body}</span>
          <span className={`ncard-in${new Date(n.starts_at).getTime() <= now ? ' is-on' : ''}`}>{startsIn(n.starts_at, now)}</span>
        </span>
      </div>
    );
  }
  return (
    <p className={`nline topic-${n.topic} tone-${n.tone}`}>
      <span className="nline-tag">{tag}</span>
      <span>{n.body}</span>
      <span className="nline-meta">{[about, c.venue?.name, n.at ? fmtTime(n.at) : 'now'].filter(Boolean).join(' · ')}</span>
    </p>
  );
}

/** "ষষ্ঠী · Shashti · Fri 16 Oct · 10:36 AM" (the Pujo day only on Pujo days). */
export function Today({ sittings, now, className = 'live-today' }: { sittings: Sitting[]; now: number; className?: string }) {
  const d = today(sittings, now);
  return (
    <time className={className} dateTime={d.iso}>
      {d.bn && <span className="today-bn">{d.bn}</span>}
      {d.day && d.day !== d.bn && <span>{d.day}</span>}
      <span>{d.date}</span>
      <b>{d.time}</b>
    </time>
  );
}

const TICK_LABEL = ['days', 'hours', 'min', 'sec'];

/** A timer that ticks every second: days, hours, minutes, seconds. */
export function Ticker({ to }: { to: string }) {
  const now = useNow(1000);
  const { d, h, m, s } = timeLeft(to, now);
  return (
    <div className="ticker" role="timer" aria-label={`${d} days ${h} hours ${m} minutes`}>
      {[d, h, m, s].map((n, i) => (
        <span key={TICK_LABEL[i]} className="tcell"><b>{String(n).padStart(2, '0')}</b><small>{TICK_LABEL[i]}</small></span>
      ))}
    </div>
  );
}

const lcFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** Before Pujo on the TV board: a live timer to Mahalaya, then to ONE10 Pujo, and where the Pujo is. */
export function BoardCountdown({ c, place }: { c: Countdown; place?: Venue }) {
  const pujoDay = (
    <p className="cd-sub">
      {c.pujo.day && <><span className="today-bn">{c.pujo.day.day_label_bn}</span> {c.pujo.day.day_label} · </>}{fmtWeekday(c.pujo.at)}
      {place && <> · <b>{place.name}</b>{place.where_text && `, ${lcFirst(place.where_text)}`}</>}
    </p>
  );
  return (
    <section className={`cd phase-${c.phase}`} aria-label="Countdown">
      {c.phase === 'mahalaya' ? (
        <>
          <p className="cd-eyebrow">মহালয়া আসছে · Countdown to Mahalaya</p>
          <Ticker to={c.target} />
          <p className="cd-sub">{fmtWeekday(c.mahalaya.at)} · Mahishasuramardini at {fmtTime(c.mahalaya.at)}</p>
          <div className="cd-then">
            <p className="cd-then-h">{c.name} starts in <b>{daysText(c.pujo.days)}</b></p>
            {pujoDay}
          </div>
        </>
      ) : (
        <>
          {c.phase === 'mahalaya-day' && <p className="cd-greet">শুভ মহালয়া · Shubho Mahalaya</p>}
          <p className="cd-eyebrow">{c.name} starts in</p>
          <Ticker to={c.target} />
          {pujoDay}
        </>
      )}
    </section>
  );
}

const daysToGo = (n: number) => `${daysText(n)}${n > 1 ? ' to go' : ''}`;

/** Before Pujo in the Pujo Live banner: days to Mahalaya and to ONE10 Pujo. */
export function CountdownTiles({ c }: { c: Countdown }) {
  return (
    <div className="cd-tiles">
      {c.phase !== 'pujo' && (
        <div className={`cd-tile${c.phase === 'mahalaya-day' ? ' is-today' : ''}`}>
          <span className="cd-tile-h">মহালয়া · Mahalaya</span>
          <b>{c.phase === 'mahalaya-day' ? 'শুভ মহালয়া' : daysToGo(c.mahalaya.days)}</b>
          <small>{c.phase === 'mahalaya-day' ? 'Today' : fmtWeekday(c.mahalaya.at)}</small>
        </div>
      )}
      <div className="cd-tile">
        <span className="cd-tile-h">{c.name}</span>
        <b>{daysToGo(c.pujo.days)}</b>
        <small>{c.pujo.day && <><span className="today-bn">{c.pujo.day.day_label_bn}</span> · </>}{fmtWeekday(c.pujo.at)}</small>
      </div>
    </div>
  );
}

/** Where things are, for the TV board: Puja Prangan, then each hall with what it serves. */
export function Places({ venues, menu }: { venues: Venue[]; menu: MenuItem[] }) {
  const list = places(venues, menu);
  if (!list.length) return null;
  return (
    <section className="board-places" aria-label="Places">
      {list.map(({ venue: v, kinds }) => (
        <div key={v.id} className="bplace">
          <p className="bplace-what">{kinds.length ? <>{kinds.map((k) => <VegMark key={k} kind={k} />)}{kindsText(kinds)}</> : v.name_bn}</p>
          <p className="bplace-name">{v.name}</p>
          {v.where_text && <p className="bplace-where">{v.where_text}</p>}
        </div>
      ))}
    </section>
  );
}

const MAX_NOTICES = 4;

/**
 * "Pujo Live" at the top of My passes and the passes link: meals being served (or about to be) and where,
 * Food Committee notices, and what's next. Hidden when there is nothing to say.
 */
export function LiveStrip({ cutoffs = false, link = true }: { cutoffs?: boolean; link?: boolean }) {
  const { feed, now, countdown: cd, ready, sittings, venues } = useLiveFeed();
  if (!ready) return null;
  const { meals, notices, next, live } = feed;
  const cut = cutoffs ? feed.cutoffs : [];
  if (!meals.length && !notices.length && !next && !cut.length && !cd) return null;
  const quiet = !meals.length && !notices.length;
  return (
    <section className={`live${live ? ' is-live' : ''}`} aria-label="Live">
      <div className="live-head">
        <p className="live-eyebrow">
          {live ? <><span className="live-dot" aria-hidden="true" />সরাসরি · Live</> : meals.length ? 'Opening soon' : cd ? `${cd.name} is coming` : 'Up next'}
        </p>
        <Today sittings={sittings} now={now} />
      </div>
      <div className="live-body" aria-live="polite">
        {cd && !meals.length && <CountdownTiles c={cd} />}
        {meals.map((m) => (
          <div className="live-meal" key={m.sitting.id}>
            <p className="live-title">{mealHeadline(m)}</p>
            <VenueChips venues={m.venues} sitting={m.sitting} link={link} />
          </div>
        ))}
        {notices.slice(0, MAX_NOTICES).map((n) => <NoticeCard key={n.id} n={n} sittings={sittings} venues={venues} now={now} />)}
        {notices.length > MAX_NOTICES && <Link className="live-next" to="/live">{notices.length - MAX_NOTICES} more on the live board ›</Link>}
        {quiet && next && (
          <div className="live-meal">
            <p className="live-title">{mealTitle(next.sitting)}</p>
            <p className="live-when">{whenText(next.sitting.opens_at, now)}</p>
            <VenueChips venues={next.venues} sitting={next.sitting} link={link} />
          </div>
        )}
      </div>
      {!quiet && next && (
        <p className="live-next">Next: <b>{mealTitle(next.sitting)}</b> · {whenText(next.sitting.opens_at, now)}</p>
      )}
      {cut.map((s) => (
        <p className="live-next" key={s.id}>
          Booking for <b>{mealTitle(s)}</b> closes {whenText(s.booking_closes_at, now)} · <Link to="/book">Book</Link>
        </p>
      ))}
      {cd && !meals.length && link && <Link className="live-next" to="/live">Watch the live countdown ›</Link>}
    </section>
  );
}
